import React, { useState, useEffect, useCallback } from 'react';
import {
  INTRO_QUESTION,
  pickRandomScoredQuestions,
  getResultLabel,
  getRecommendations,
  SCORES,
  SCORED_QUESTIONS
} from './JuniorCallsData';
import './JuniorCalls.css';

const DRAFT_STORAGE_KEY = 'junior_call_draft';
const HOURS_0_23 = Array.from({ length: 24 }, (_, i) => i);

function restoreQuestionsFromIds(questionIds, allQuestions = null) {
  if (!Array.isArray(questionIds) || questionIds.length !== 12) return null;
  
  // Если есть вопросы из БД, используем их
  if (allQuestions && allQuestions.length > 0) {
    const questions = questionIds
      .map((id) => allQuestions.find((q) => q.id === id))
      .filter(Boolean);
    return questions.length === 12 ? questions : null;
  }
  
  // Fallback на статические данные
  const questions = questionIds
    .map((id) => SCORED_QUESTIONS.find((q) => q.id === id))
    .filter(Boolean);
  return questions.length === 12 ? questions : null;
}

/**
 * Сценарий обзвона: вводный вопрос + 12 случайных с оценкой 0/0.5/1.
 * moderator: { nickname, discord_id?, role? }
 * initialDraft: { step, questionIds, answers } — восстановление после перезагрузки
 * onClose: () => void — по завершении или отмене
 * onComplete: (score: number) => void — вызывается при нажатии «Закрыть» после результата (перед onClose)
 * isModal: boolean — если true, кнопка "Завершить" говорит "Закрыть"
 */
const JuniorCallFlow = ({ moderator, initialDraft, onClose, onComplete, isModal = false }) => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const restored = initialDraft && restoreQuestionsFromIds(initialDraft.questionIds);
  const [step, setStep] = useState(restored ? initialDraft.step : 0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState(() => (restored && initialDraft.answers ? initialDraft.answers : []));
  const [selectedScore, setSelectedScore] = useState(null);
  const [primeHours, setPrimeHours] = useState([]);
  const [primeHoursWeekend, setPrimeHoursWeekend] = useState([]);
  const [primeTimesLoading, setPrimeTimesLoading] = useState(false);
  const [primeTimesSaving, setPrimeTimesSaving] = useState(false);

  // Загружаем вопросы из БД
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setQuestionsLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch('/api/junior-call-questions', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAllQuestions(data);
          
          // Если есть восстановленный черновик, используем его
          if (restored && initialDraft.questionIds) {
            const restoredQuestions = restoreQuestionsFromIds(initialDraft.questionIds, data);
            if (restoredQuestions && restoredQuestions.length === 12) {
              setQuestions(restoredQuestions);
              setQuestionsLoading(false);
              return;
            }
          }
          
          // Иначе выбираем случайные вопросы
          const scoredQuestions = data.filter(q => !q.is_intro);
          
          // Перемешиваем и выбираем 12 случайных
          const shuffled = [...scoredQuestions].sort(() => Math.random() - 0.5);
          const selected = shuffled.slice(0, 12);
          
          setQuestions(selected);
        } else {
          // Fallback на статические данные
          console.warn('Failed to load questions from DB, using static data');
          if (restored && initialDraft.questionIds) {
            const restoredQuestions = restoreQuestionsFromIds(initialDraft.questionIds);
            if (restoredQuestions) {
              setQuestions(restoredQuestions);
            } else {
              setQuestions(pickRandomScoredQuestions());
            }
          } else {
            setQuestions(pickRandomScoredQuestions());
          }
        }
      } catch (error) {
        console.error('Error loading questions:', error);
        // Fallback на статические данные
        if (restored && initialDraft.questionIds) {
          const restoredQuestions = restoreQuestionsFromIds(initialDraft.questionIds);
          if (restoredQuestions) {
            setQuestions(restoredQuestions);
          } else {
            setQuestions(pickRandomScoredQuestions());
          }
        } else {
          setQuestions(pickRandomScoredQuestions());
        }
      } finally {
        setQuestionsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  useEffect(() => {
    if (!moderator?.discord_id || !questions?.length) return;
    const draft = {
      moderator: { discord_id: moderator.discord_id, nickname: moderator.nickname },
      step,
      questionIds: questions.map((q) => q.id),
      answers
    };
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
      console.warn('Junior call draft save failed', e);
    }
  }, [moderator, step, questions, answers]);

  const handleIntroNext = () => setStep(1);

  const handlePrevQuestion = () => {
    if (step <= 1) return;
    const lastAnswer = answers[answers.length - 1];
    setStep(step - 1);
    setAnswers(answers.slice(0, -1));
    setSelectedScore(lastAnswer != null ? lastAnswer.score : null);
  };

  const handleScoredAnswer = (score) => {
    const currentQuestion = questions[step - 1];
    const newAnswers = [...answers, { questionId: currentQuestion.id || currentQuestion.id, score }];
    const nextStep = step + 1;
    if (nextStep > questions.length) {
      setAnswers(newAnswers);
      setStep('result');
    } else {
      setAnswers(newAnswers);
      setStep(nextStep);
    }
  };

  const handleNextQuestion = () => {
    if (selectedScore === null) return;
    handleScoredAnswer(selectedScore);
    setSelectedScore(null);
  };

  const totalScore = answers.reduce((sum, a) => sum + a.score, 0);
  const currentDisplayScore = totalScore + (selectedScore !== null ? selectedScore : 0);
  const resultInfo = step === 'result' ? getResultLabel(totalScore) : null;
  const isIntro = step === 0;
  const isResult = step === 'result';
  const showPrimeTimes = isResult && totalScore >= 5.5;

  const progressPercent = isIntro ? 0 : isResult ? 100 : (step / questions.length) * 100;

  useEffect(() => {
    if (!showPrimeTimes || !moderator?.discord_id) return;
    let cancelled = false;
    setPrimeTimesLoading(true);
    const token = localStorage.getItem('token');
    fetch(`/api/prime-times/${encodeURIComponent(moderator.discord_id)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => (res.ok ? res.json() : { hours: [], hours_weekend: [] }))
      .then((data) => {
        if (!cancelled) {
          setPrimeHours(Array.isArray(data.hours) ? data.hours : []);
          setPrimeHoursWeekend(Array.isArray(data.hours_weekend) ? data.hours_weekend : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPrimeHours([]);
          setPrimeHoursWeekend([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPrimeTimesLoading(false);
      });
    return () => { cancelled = true; };
  }, [showPrimeTimes, moderator?.discord_id]);

  const togglePrimeHour = useCallback((hour, isWeekend) => {
    const setter = isWeekend ? setPrimeHoursWeekend : setPrimeHours;
    const current = isWeekend ? primeHoursWeekend : primeHours;
    setter(
      current.includes(hour) ? current.filter((h) => h !== hour) : [...current, hour].sort((a, b) => a - b)
    );
  }, [primeHours, primeHoursWeekend]);

  const savePrimeTimes = useCallback(async () => {
    if (!moderator?.discord_id || primeTimesSaving) return;
    setPrimeTimesSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/prime-times', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          moderator_discord_id: moderator.discord_id,
          hours: primeHours,
          hours_weekend: primeHoursWeekend
        })
      });
    } catch (e) {
      console.error('Save prime times error', e);
    } finally {
      setPrimeTimesSaving(false);
    }
  }, [moderator?.discord_id, primeHours, primeHoursWeekend, primeTimesSaving]);

  return (
    <div className={`junior-calls-call-view ${isModal ? 'junior-calls-call-view-modal' : ''}`}>
      <div className="junior-calls-call-card">
        <div className="junior-calls-call-header">
          <h2 className="junior-calls-call-title">Проведение обзвона — опроса для модератора: {moderator.nickname}</h2>
          {!isIntro && !isResult && (
            <>
              <span className="junior-calls-call-step">Вопрос {step} из {questions.length}</span>
              <span className="junior-calls-call-score">Набрано баллов: {currentDisplayScore}</span>
            </>
          )}
          {!isResult && (
            <div className="junior-calls-progress-bar-wrap">
              <div className="junior-calls-progress-bar" style={{ width: `${progressPercent}%` }} />
            </div>
          )}
        </div>

        {questionsLoading && (
          <div className="junior-calls-loading">
            <div className="loader"></div>
            <p>Загрузка вопросов...</p>
          </div>
        )}

        {!questionsLoading && isIntro && (
          <div className="junior-calls-question-block junior-calls-intro-block">
            <div className="junior-calls-question-label junior-calls-question-label-intro">Разминка</div>
            <p className="junior-calls-question-text junior-calls-intro-text">
              {allQuestions.length > 0 
                ? (allQuestions.find(q => q.is_intro)?.question_text || INTRO_QUESTION.text)
                : INTRO_QUESTION.text}
            </p>
            <p className="junior-calls-intro-note">Вопрос не оценивается — просто обсудите с модератором</p>
            <button type="button" className="junior-calls-btn junior-calls-btn-next" onClick={handleIntroNext}>
              <span>Перейти к вопросам</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          </div>
        )}
        
        {!questionsLoading && !isIntro && !isResult && questions[step - 1] && (
          <div className="junior-calls-question-block">
            <div className="junior-calls-question-card">
              <p className="junior-calls-question-text">{questions[step - 1].question_text || questions[step - 1].text}</p>
            </div>
            <div className="junior-calls-answer-ref">
              <span className="junior-calls-answer-ref-icon">✓</span>
              <div>
                <strong>Эталон ответа</strong>
                <p>{questions[step - 1].answer_text || questions[step - 1].answer}</p>
              </div>
            </div>
            <div className="junior-calls-scores-wrap">
              <span className="junior-calls-scores-label">Выберите оценку:</span>
              <div className="junior-calls-scores">
                {SCORES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`junior-calls-score-btn junior-calls-score-${s === 0 ? 'zero' : s === 0.5 ? 'half' : 'one'} ${selectedScore === s ? 'junior-calls-score-btn-selected' : ''}`}
                    onClick={() => setSelectedScore(s)}
                  >
                    <span className="junior-calls-score-value">{s === 0.5 ? '0.5' : s}</span>
                    <span className="junior-calls-score-unit">балл{s === 1 ? '' : s === 0.5 ? 'а' : 'ов'}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="junior-calls-question-nav">
              <div className="junior-calls-question-nav-left">
                {step > 1 ? (
                  <button type="button" className="junior-calls-btn junior-calls-btn-prev" onClick={handlePrevQuestion}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    <span>Предыдущий вопрос</span>
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className="junior-calls-btn junior-calls-btn-next"
                onClick={handleNextQuestion}
                disabled={selectedScore === null}
              >
                <span>Следующий вопрос</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}

        {isResult && (
          <div className="junior-calls-result-block">
            <div className={`junior-calls-result-badge junior-calls-result-${resultInfo.status}`}>
              {resultInfo.label}
            </div>
            <div className="junior-calls-result-score">
              <span className="junior-calls-result-score-value">{totalScore}</span>
              <span className="junior-calls-result-score-divider">из 12</span>
            </div>
            <div className="junior-calls-result-recommendations">
              <h4 className="junior-calls-result-recommendations-title">Рекомендации</h4>
              <ul className="junior-calls-result-recommendations-list">
                {getRecommendations(totalScore).map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </div>
            {showPrimeTimes && (
              <div className="junior-calls-prime-times-block">
                <h4 className="junior-calls-prime-times-title">Прайм-таймы обзваниваемого</h4>
                <p className="junior-calls-prime-times-hint">Обязательно узнайте у модератора удобные часы для модерации и отметьте их (для будней и выходных отдельно).</p>
                {primeTimesLoading ? (
                  <div className="junior-calls-prime-times-loading">Загрузка...</div>
                ) : (
                  <>
                    <div className="junior-calls-prime-times-row">
                      <span className="junior-calls-prime-times-label junior-calls-prime-times-weekday">Будни</span>
                      <div className="junior-calls-prime-times-grid-wrap">
                        <div className="junior-calls-prime-times-labels">
                          {HOURS_0_23.map((h) => (
                            <span key={h} className="junior-calls-prime-time-label">{String(h).padStart(2, '0')}</span>
                          ))}
                        </div>
                        <div className="junior-calls-prime-times-hours">
                          {HOURS_0_23.map((h) => (
                            <button
                              key={h}
                              type="button"
                              className={`junior-calls-prime-hour-btn ${primeHours.includes(h) ? 'active' : ''}`}
                              onClick={() => togglePrimeHour(h, false)}
                              title={`${String(h).padStart(2, '0')}:00`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="junior-calls-prime-times-row">
                      <span className="junior-calls-prime-times-label junior-calls-prime-times-weekend">Выходные</span>
                      <div className="junior-calls-prime-times-grid-wrap">
                        <div className="junior-calls-prime-times-labels">
                          {HOURS_0_23.map((h) => (
                            <span key={h} className="junior-calls-prime-time-label">{String(h).padStart(2, '0')}</span>
                          ))}
                        </div>
                        <div className="junior-calls-prime-times-hours">
                          {HOURS_0_23.map((h) => (
                            <button
                              key={h}
                              type="button"
                              className={`junior-calls-prime-hour-btn junior-calls-prime-hour-weekend ${primeHoursWeekend.includes(h) ? 'active' : ''}`}
                              onClick={() => togglePrimeHour(h, true)}
                              title={`${String(h).padStart(2, '0')}:00`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <button
              type="button"
              className="junior-calls-btn junior-calls-btn-back"
              disabled={showPrimeTimes && primeTimesSaving}
              onClick={async () => {
                if (showPrimeTimes) await savePrimeTimes();
                if (typeof onComplete === 'function') await onComplete(totalScore);
                onClose();
              }}
            >
              {showPrimeTimes && primeTimesSaving ? 'Сохранение...' : isModal ? 'Закрыть' : 'Завершить и вернуться к списку'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JuniorCallFlow;
